import React, { useCallback, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchVehicle, postRemoteAction } from "../api/vehicle";
import useAssistance from "../hooks/useAssistance";
import { useAuth } from "../../hooks";

export const VehicleContext = React.createContext({});

/**
 * The vehicle context provider
 * 
 * @param {Object} props The props of the component
 * @returns The vehicle context provider
 */
const VehicleProvider = ({ children }) => {
  const { assistance } = useAssistance();
  const { fetchBearer } = useAuth();

  const vehicleRef = useRef(null);

  const vehicleIdentificationNumber = useMemo(
    () => assistance?.VIN || vehicleRef.current?.VIN,
    [assistance],
  );

  /**
   * Wraps the vehicle query function
   * 
   * @returns The vehicle query function
   */
  const vehicleQueryFunction = useCallback(
    async () => {
      const bearer = await fetchBearer();

      return fetchVehicle(vehicleIdentificationNumber, bearer)
    },
    [vehicleIdentificationNumber, fetchBearer],
  );

  const vehicleQuery = useQuery({
    queryKey: ["vehicle", vehicleIdentificationNumber],
    queryFn: vehicleQueryFunction,
    enabled: !!assistance?.VIN,
    refetchInterval: 1000,
  });

  const vehicle = useMemo(() => {
    if (vehicleQuery.data) {
      vehicleRef.current = vehicleQuery.data;
    }
    return vehicleRef.current;
  }, [vehicleQuery.data]);

  /**
   * Stops fetching the vehicle
   */
  const stopFetching = useCallback(() => {
    vehicleRef.current = null;
  }, []);

  const isHazardLightsOn = useMemo(
    () => !!vehicle?.Vehicle?.Body?.Lights?.Hazard?.IsSignaling,
    [vehicle?.Vehicle?.Body?.Lights?.Hazard?.IsSignaling],
  );

  /**
   * Toggles the hazard lights
   */
  const toggleHazardLights = useCallback(async () => {
    const bearer = await fetchBearer();
    
    return postRemoteAction(
      vehicleIdentificationNumber,
      {
        warningLightsSwitchedOn: !isHazardLightsOn,
        doorsAreUnlocked: false,
      },
      bearer,
    );
  }, [isHazardLightsOn, vehicleIdentificationNumber, fetchBearer]);

  /**
   * Unlocks the doors
   */
  const unlockDoors = useCallback(async () => {
    const bearer = await fetchBearer()

    return postRemoteAction(
      vehicleIdentificationNumber,
      {
        warningLightsSwitchedOn: isHazardLightsOn,
        doorsAreUnlocked: true,
      },
      bearer,
    );
  }, [isHazardLightsOn, vehicleIdentificationNumber, fetchBearer]);

  const occupants = useMemo(() => vehicle?.Vehicle?.Occupant, [vehicle]);

  const [occupantsCount, kidsCount] = useMemo(() => {
    let occupantsCount = 0;
    let kidsCount = 0;

    if (occupants?.Row1?.DriverSide?.IsOccupied) {
      occupantsCount += 1;
      if (occupants.Row1.DriverSide.Age === "child") {
        kidsCount += 1;
      }
    }
    if (occupants?.Row1?.PassengerSide?.IsOccupied) {
      occupantsCount += 1;
      if (occupants.Row1.PassengerSide.Age === "child") {
        kidsCount += 1;
      }
    }
    if (occupants?.Row2?.DriverSide?.IsOccupied) {
      occupantsCount += 1;
      if (occupants.Row2.DriverSide.Age === "child") {
        kidsCount += 1;
      }
    }
    if (occupants?.Row2?.Middle?.IsOccupied) {
      occupantsCount += 1;
      if (occupants.Row2.Middle.Age === "child") {
        kidsCount += 1;
      }
    }
    if (occupants?.Row2?.PassengerSide?.IsOccupied) {
      occupantsCount += 1;
      if (occupants.Row2.PassengerSide.Age === "child") {
        kidsCount += 1;
      }
    }
    return [occupantsCount, kidsCount];
  }, [occupants]);

  const contextValue = useMemo(
    () => ({
      isHazardLightsOn,
      stopFetching,
      toggleHazardLights,
      unlockDoors,
      vehicle,
      occupants,
      occupantsCount,
      kidsCount,
    }),
    [isHazardLightsOn, stopFetching, toggleHazardLights, unlockDoors, vehicle, occupants, occupantsCount, kidsCount],
  );

  return (
    <VehicleContext.Provider value={contextValue}>
      {children}
    </VehicleContext.Provider>
  );
};

export default React.memo(VehicleProvider);
